import {RoxxTokenizer, RoxxTokenTypeRand, RoxxTokenTypeRator} from './RoxxTokenizer'

import RoxLogger from '../lib/RoxLogger'
import * as RoxxOperatorsMap from '../lib/RoxxOperators'
import globalUserUnhandledErrorInvoker from '../lib/UserspaceUnhandledErrorInvoker'
import Config from '../config'

export default class RoxxParser {
  _tokenizer
  _cache
  /**
   * A parser for Roxx expressions.
   * Roxx expression are polish notation expressions {@link https://en.wikipedia.org/wiki/Polish_notation}
   * @class
   * @module RoxxParser
   * @param {*} cache - Optional token cache object. A default cache object is used if none specified.
   */
  constructor(cache?) {
    this._tokenizer = new RoxxTokenizer()
    this._cache = cache || {}
  }

  /**
   * Given an operator function and stack, return an array of arguments for the operator.
   * @param {Function} operator
   * @param {Array} stack
   * @returns {Array} Array of arguments for operator
   * @private
   */
  _argsArrayForOperator(operator, stack) {
    const argsArray = []
    const length = operator.length
    for (let i = 0; i < length; i++) {
      const arg = stack.pop()
      argsArray.push(arg)
    }

    return argsArray
  }

  /**
   * Add context to specific operators after operator's args args.
   * @param {*} operator, args, context
   * @returns {Array} Args array with/without context
   */
  _modifyArgsHook({operator, args, context, callContext}) {
    let argsWithContext = args
    if (context && RoxxOperatorsMap.operatorsWithContext.includes(operator)) {
      argsWithContext = [...args, context] // insert context
    }
    if (callContext) {
      return [...argsWithContext, callContext]
    }

    return argsWithContext
  }

  /**
   * Tokenizes and caches expr if not available in cache.
   * @param {string} expr
   * @returns {Array} Tokenized version of expr
   */
  compileExpression(expr) {
    let tokens = this._cache[expr]
    if (!tokens) {
      tokens = this._tokenizer.tokenize(expr).reverse()
      this._cache[expr] = tokens
    }
    return tokens
  }

  /**
   * Evaluates a Roxx expression.
   *
   * @param {string} expr - Roxx expression string.
   * @returns {*} Result of Roxx expression evaluation.
   */
  evaluateExpression(expr, callContext = {}, context = {}, collector?) {
    const stack = []
    const tokens = this.compileExpression(expr)

    let result = undefined
    const length = tokens.length
    try {
      for (let i = 0; i < length; i++) {
        const token = tokens[i]
        if (token.type == RoxxTokenTypeRand) {
          stack.push(token.value)
        } else if (token.type == RoxxTokenTypeRator) {
          const operator = RoxxOperatorsMap[token.value]
          let args = this._argsArrayForOperator(operator, stack)
          args = this._modifyArgsHook({operator, args, context, callContext})
          const value = operator.apply(this, args)
          stack.push(value)
          const debugSentence = `${token.value}(${JSON.stringify(args)}) => ${value}`
          if (collector) {
            collector.push(debugSentence)
          }
          RoxLogger.debug(`Roxx: ${debugSentence}`)
        } else {
          stack.push(undefined)
          break
        }
      }
      result = stack.pop()
    } catch (err) {
      const message = `Uh oh! An error occurred during Roxx evaluation. ${expr}`

      if (err.isUserError) {
        globalUserUnhandledErrorInvoker.invoke(err.trigger, err)
      } else {
        Config.getErrorReporter()?.error(message, err)
      }
      RoxLogger.error(message, err)

      result = false
    } finally {
      return result
    }
  }
}
